import { useEffect, useCallback } from 'react';
import { signInWithEmailAndPassword, signOut, onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { auth, db } from '@/firebase-client';
import { useSKClubStore } from './useSKClubStore';
import toast from 'react-hot-toast';

export default function useSKClubAuth() {
  const { firebaseUser, loading, skPerks, coinBalance, setFirebaseUser, setLoading, setSkPerks, setCoinBalance, reset } = useSKClubStore();

  useEffect(() => {
    const unsubAuth = onAuthStateChanged(auth, async (user) => {
      setFirebaseUser(user);
      setLoading(false);

      if (!user) {
        reset();
        return;
      }

      try {
        const userDoc = await getDoc(doc(db, 'sk_club_users', user.uid));
        if (userDoc.exists()) {
          const data = userDoc.data();
          setSkPerks(data.skPerks || null);
        }
      } catch (error) {
        console.error('Failed to fetch SK Club data:', error);
      }
    });

    return () => unsubAuth();
  }, []);

  useEffect(() => {
    if (!firebaseUser) return;

    const unsubProgress = onSnapshot(
      doc(db, 'sk_club_users', firebaseUser.uid, 'data', 'progress'),
      (docSnap) => {
        if (docSnap.exists()) {
          setCoinBalance(docSnap.data().coins || 0);
        }
      },
      (error) => {
        console.error('Failed to listen to progress:', error);
      }
    );

    return () => unsubProgress();
  }, [firebaseUser]);

  const login = useCallback(async (email, password) => {
    setLoading(true);
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (error) {
      console.error(error.message);
      let message = '';
      if (error.code === 'auth/user-not-found') {
        message = 'User not found. Make sure you linked your account in the mobile app first.';
      } else if (error.code === 'auth/wrong-password') {
        message = 'Wrong password';
      } else if (error.code === 'auth/invalid-credential') {
        message = 'Invalid email or password';
      } else {
        message = 'Something went wrong. Please try again later.';
      }
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    await signOut(auth);
    reset();
  }, []);

  const getIdToken = useCallback(async () => {
    if (!firebaseUser) return null;
    return await firebaseUser.getIdToken();
  }, [firebaseUser]);

  return {
    firebaseUser,
    loading,
    skPerks,
    coinBalance,
    login,
    logout,
    getIdToken,
    isAuthenticated: !!firebaseUser,
  };
}
